import React, {useCallback, useMemo} from 'react';
import {Stack} from 'react-bootstrap';
import {MiniRadio} from 'shared/components/Forms/MiniRadio';
import styled, {useTheme} from 'styled-components';
import {PayButton} from 'components/customer/Confirmation/Gateways/PayButton';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {PaymentMethod} from 'components/customer/Confirmation/Payment/entity/PaymentSession';
import {
    paymentMethodSet,
    selectFullAmount,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {PaymentMethodType} from 'components/manufacturer/Payments/entity/Setting';
import {formatPrice} from 'shared/helpers';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {PaymentType} from 'components/manufacturer/Payments/entity/Deposit';
import {MoreInfo} from 'shared/components/MoreInfo/MoreInfo';
import {mobile, smallDevices} from 'shared/Breakpoints';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';

interface PaymentOptionProps {
    selected: boolean;
    method: PaymentMethod;
    readOnly?: boolean;
    paymentType: PaymentType;
}

export const PaymentOption = ({
    selected,
    method,
    readOnly = false,
    paymentType,
}: PaymentOptionProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const {job} = useJobContext() as PartialJob;
    const fullAmount = useAppSelector(selectFullAmount);

    const handlePaymentOptionSelection = useCallback(
        (paymentMethod: PaymentMethod) => () => {
            if (!readOnly) {
                dispatch(paymentMethodSet(paymentMethod.paymentType));
            }
        },
        [readOnly]
    );

    const {isDeposit, isBalance} = useMemo(() => {
        return {
            isDeposit:
                method.paymentType == PaymentMethodType.CARD_DEPOSIT ||
                method.paymentType == PaymentMethodType.INVOICE_DEPOSIT,
            isBalance: method.paymentType == PaymentMethodType.BALANCE,
        };
    }, [method]);

    const {jobTotal, tax} = useMemo(() => {
        if (isDeposit || method.paymentType == PaymentMethodType.BALANCE) {
            const taxRate = Number(job?.taxRate ?? 10);
            const tax =
                taxRate > 0 ? (fullAmount * taxRate) / (100 + taxRate) : 0;

            return {
                jobTotal: fullAmount - tax,
                tax,
            };
        }

        return {
            jobTotal: (method?.baseAmount || 0) - (method?.taxAmount || 0),
            tax: method?.taxAmount,
        };
    }, [method, isDeposit, job, fullAmount]);

    const {paidAmount} = useMemo(() => {
        if (method.paymentType == PaymentMethodType.BALANCE) {
            const paidAmount = fullAmount - method?.baseAmount;

            return {
                paidAmount,
            };
        }

        return {
            paidAmount: 0,
        };
    }, [method, fullAmount]);

    const bigText = useMemo(() => {
        if (
            method.paymentType == PaymentMethodType.CARD_DEPOSIT ||
            method.paymentType == PaymentMethodType.INVOICE_DEPOSIT
        ) {
            return 'Pay Deposit';
        } else if (method.paymentType == PaymentMethodType.ACCOUNT) {
            return 'Total';
        } else if (method.paymentType == PaymentMethodType.BALANCE) {
            return 'Pay Balance';
        } else {
            return 'Pay in full';
        }
    }, [method]);

    return (
        <Card
            $readOnly={readOnly}
            $selected={selected}
            onClick={handlePaymentOptionSelection(method)}>
            <PriceContainer>
                <InfoContainer>
                    <Header>
                        {!readOnly ? (
                            <RadioButton
                                $checked={selected}
                                $round
                                $coreColor={theme.colors.primary.main}
                            />
                        ) : null}
                        {bigText}
                    </Header>
                    <BigCost>
                        {formatPrice(method.totalWithSurcharge, {})}
                    </BigCost>
                </InfoContainer>
                <Line />
                <ItemContainer>
                    <OrderSummary>Order summary</OrderSummary>
                    <Item direction="horizontal" $bold>
                        <div>Item</div>
                        <div>Price</div>
                    </Item>
                    <Item direction="horizontal">
                        <div>
                            <OverlayTrigger overlay="View job details PDF">
                                <a
                                    href={`/v2/pdf/preview/${job.id}`}
                                    target="_blank"
                                    rel="noreferrer">
                                    Job order #{job.displayId}
                                </a>
                            </OverlayTrigger>
                        </div>
                        <div>{formatPrice(jobTotal, {})}</div>
                    </Item>
                    <Item direction="horizontal">
                        <div>GST</div>
                        <div>{formatPrice(tax || 0, {})}</div>
                    </Item>

                    {isBalance ? (
                        <>
                            <Item direction="horizontal" $bold>
                                <div>Total</div>
                                <div>{formatPrice(tax + jobTotal, {})}</div>
                            </Item>
                            <Item
                                direction="horizontal"
                                $red
                                className="mb-3 mt-3">
                                <div>Deposit paid</div>
                                <div>-{formatPrice(paidAmount, {})}</div>
                            </Item>
                            <Item direction="horizontal" $bold>
                                <div>Balance</div>
                                <div>{formatPrice(method?.baseAmount, {})}</div>
                            </Item>
                        </>
                    ) : null}

                    {isDeposit ? (
                        <Item direction="horizontal" $bold className="mb-3">
                            <div>Total</div>
                            <div>{formatPrice(method?.fullAmount, {})}</div>
                        </Item>
                    ) : null}
                    {paymentType == PaymentType.GATEWAY && isDeposit ? (
                        <Item direction="horizontal" $bold>
                            <div>
                                {method?.depositPercentage}% Deposit of Total
                            </div>
                            <div>{formatPrice(method?.baseAmount, {})}</div>
                        </Item>
                    ) : null}
                    {paymentType == PaymentType.GATEWAY &&
                    method?.surchargeAmount != 0 ? (
                        <Item direction="horizontal">
                            <div>Payment processing fee</div>
                            <MoreInfo
                                hoverText={
                                    <MoreInfoContent>
                                        <li>
                                            <strong>
                                                Card processing fee:
                                            </strong>{' '}
                                            {formatPrice(
                                                method?.surchargeAmount || 0,
                                                {}
                                            )}
                                        </li>

                                        <li>
                                            <strong>Platform fee:</strong>{' '}
                                            {formatPrice(
                                                method?.platformFee || 0,
                                                {}
                                            )}
                                        </li>
                                    </MoreInfoContent>
                                }
                                dynamicposition
                            />
                            <div>
                                {formatPrice(
                                    (method?.surchargeAmount || 0) +
                                        (method?.platformFee || 0),
                                    {}
                                )}
                            </div>
                        </Item>
                    ) : null}
                    <Item direction="horizontal" $bold>
                        <div>
                            Total{' '}
                            {isDeposit
                                ? paymentType == PaymentType.GATEWAY
                                    ? 'due now'
                                    : `due now (${method.depositPercentage}% of total)`
                                : 'due'}
                        </div>
                        <div>{formatPrice(method.totalWithSurcharge, {})}</div>
                    </Item>
                </ItemContainer>
            </PriceContainer>
            {method.paymentType == PaymentMethodType.CARD_DEPOSIT ||
            method.paymentType == PaymentMethodType.INVOICE_DEPOSIT ? (
                <>
                    <HorizontalLine />
                    <Subtext>
                        <strong>What happens next?</strong>
                        <p>
                            Remaining{' '}
                            {formatPrice(method?.remainingBalance, {})} will be
                            charged once production is completed, prior to goods
                            being released.
                        </p>
                    </Subtext>
                </>
            ) : null}

            {selected && !readOnly ? <PayButton /> : null}
        </Card>
    );
};

const RadioButton = styled(MiniRadio)`
    display: inline-block;
    height: 20px;
`;

const OrderSummary = styled.div`
    font-weight: 600;
`;

const Line = styled.div`
    border-left: 1px solid rgba(255, 255, 255, 0.3);
`;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 8.25rem;
`;

const Header = styled.div`
    font-size: 1.2em;
    margin-bottom: 5px;
    font-weight: 400;
`;

const Subtext = styled.div`
    font-size: 0.8em;

    > span {
        text-decoration: underline;
        font-weight: 500;
    }

    > p {
        margin-bottom: 0;
    }
`;

const BigCost = styled.div`
    text-align: center;
    font-size: 1.5em;
    display: flex;
    align-items: end;
    justify-content: center;
    font-weight: 500;
`;

const ItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
`;

const PriceContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 15px;
    width: 100%;

    > div:first-child {
        font-size: 1em;
        font-weight: 300;
    }

    > div:nth-child(2) {
        border-color: rgba(109, 110, 120, 0.35);
    }

    ${mobile`
        flex-direction: column;    
    `}
`;

const Card = styled.div<{$selected?: boolean; $readOnly: boolean}>`
    cursor: pointer;
    border-radius: 8px;
    box-shadow:
        -15px -15px 10px 7px white,
        4px 4px 10px -4px #c1c1c1;
    background: #f1f1f1;
    padding: 20px;
    color: #6d6e78;
    transition:
        transform 200ms ease-in-out,
        box-shadow 500ms;

    ${({$readOnly}) => {
        if (!$readOnly) {
            return `&:hover {
                transform: translateY(-2px);
                box-shadow:
                    0px -19px 20px -13px white,
                    0px 5px 17px 0px #c1c1c1;
            }`;
        }
    }}

    display: flex;
    flex-direction: column;
    gap: 15px;
    align-items: center;

    ${({$selected, theme}) => {
        if ($selected) {
            return `
                border: 2px solid ${theme.colors.primary.main};
                background: white;
            `;
        }
    }}

    ${smallDevices`
        box-shadow: none;    
    `}
`;

const Item = styled(Stack)<{$bold?: boolean; $red?: boolean}>`
    ${({$bold = false}) => {
        if ($bold) {
            return `font-weight: bold;`;
        }
    }}

    ${({$red = false}) => {
        if ($red) {
            return `color: #bd0404;`;
        }
    }}

    justify-content: space-between;
    align-items: center;
    border-bottom: 1px dotted #6d6e78;
    font-size: 0.9em;

    &:last-child {
        border-bottom: none;
    }
`;

const HorizontalLine = styled.div`
    border-top: 1px solid rgba(109, 110, 120, 0.35);
    width: 70%;
`;

const MoreInfoContent = styled.ul`
    margin: 0;
    padding: 0 0 0 15px;
    text-align: left;
    font-weight: 400;
    font-size: 0.9em;
`;
